import { isEqual } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { WarehouseScheduleParamsCacheActions as CacheActions } from 'store/ducks/cache';
import { ListWarehouseScheduleEntriesActions as MainActions } from 'store/ducks/scheduling';
import OrderModal, { Ref as OrderModalRef } from './OrderModal';
import Filters, { FindOrders } from './Filters';
import ScheduleEntry from './ScheduleEntry';
import * as S from './styles';
import { Skeleton } from './Skeleton';
import { WarehouseScheduleEntry } from 'contracts/Scheduling';

const processSchedules = (
  scheduleEntries: WarehouseScheduleEntry[]
): WarehouseScheduleEntry[] => {
  return scheduleEntries.map((entry) => {
    const counts: { [key: string]: number } = {};
    const positionCounts: { [key: string]: number } = {};

    // Contar quantos agendamentos existem em cada horário
    entry.orders.forEach((order) => {
      const time = order.scheduledAt;
      counts[time] = (counts[time] || 0) + 1;
    });

    // Criar um novo array de ordens para evitar mutação direta
    const updatedOrders = entry.orders.map((order) => {
      const time = order.scheduledAt;

      // Contar a posição de cada ordem no horário
      positionCounts[time] = (positionCounts[time] || 0) + 1;

      return {
        ...order,
        sameTimeCount: counts[order.scheduledAt],
        sameTimePosition: positionCounts[order.scheduledAt],
      };
    });

    // Retornar um novo objeto com o array de ordens atualizado
    return {
      ...entry,
      orders: updatedOrders,
    };
  });
};

export const OrdersCalendar: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const orderModalRef = useRef<OrderModalRef>(null);
  const { data: cachedQuery } = useSelector(
    (state: RootState) => state.warehouseScheduleParamsCache
  );

  const [updatedSchedules, setUpdatedSchedules] = useState<
    WarehouseScheduleEntry[]
  >([]);

  const [query, setQuery] = useState<FindOrders>({
    status: [
      'aguardando',
      'chegada',
      'iniciado',
      'finalizado',
      'liberado',
      'noShow',
      'cancelado',
      'pendente'
    ],
    ...cachedQuery,
  });

  const { data: scheduleEntries, loading: loadingEntries } = useSelector(
    (state: RootState) => state.listWarehouseScheduleEntries
  );

  const reloadScheduleEntries = useCallback((): void => {
    dispatch(MainActions.request(query));
  }, [dispatch, query]);

  const onFilter = useCallback(
    (filter: FindOrders): void => {
      const { dirty: oldDirty, ...oldState } = query;
      const { dirty: newDirty, ...newState } = filter;
      if (isEqual(oldState, newState)) return;
      setQuery((state) => ({ ...state, ...newState }));
    },
    [query]
  );

  const onQueryChange = useCallback((): void => {
    const { warehouseId, date, status } = query;
    if (warehouseId && date && status) {
      dispatch(MainActions.request(query));
    }
  }, [dispatch, query]);

  useEffect(() => {
    onQueryChange();
  }, [onQueryChange]);

  useEffect(() => {
    return () => {
      dispatch(MainActions.reset());
      dispatch(CacheActions.updateCache(query));
    };
  }, [dispatch, query]);

  useEffect(() => {
    if (scheduleEntries) {
      setUpdatedSchedules(processSchedules(scheduleEntries));
    }
  }, [scheduleEntries]);

  return (
    <S.Container>
      <S.Panel>
        {/* <pre>{JSON.stringify(query, null, 2)}</pre> */}
        <OrderModal onUpdate={reloadScheduleEntries} ref={orderModalRef} />
        <Filters currentFilter={query} onFilter={onFilter} />
        <S.ScheduleEntries>
          {loadingEntries && <Skeleton />}
          {updatedSchedules.map((entry) => (
            <ScheduleEntry
              onSelectOrder={(orderId) =>
                orderModalRef.current?.selectOrder(orderId)
              }
              key={entry.dock.id}
              entry={entry}
            />
          ))}
        </S.ScheduleEntries>
      </S.Panel>
    </S.Container>
  );
};
